import { useState, useEffect } from 'react'
import { IBeneficiary, IConfig } from './AppExclusion.types';
import { cpfMask } from 'utils/cpf';
import { DataGrid, GridColDef, GridRowModesModel, GridRowModes, GridEventListener, GridRowId, GridRowModel, GridRowEditStopReasons, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import { documentosFaltando, opcoesDeUpload } from '../documentos-obrigatorios';
import { Button, Box, Modal, IconButton, Badge, Typography, List, ListItem, ListItemText, Chip, Select, MenuItem, TextField, FormControl, InputLabel, CircularProgress, Tooltip } from '@mui/material';
import { Close, Description as DescriptionIcon, Delete as DeleteIcon, Block as BlockIcon, CheckCircle as CheckCircleIcon, VerifiedOutlined as VerifiedIcon } from '@mui/icons-material';
import { useHistory, useParams } from 'react-router-dom';
import api from 'services/api';
import dayjs from 'dayjs';
import { useAlert } from 'contexts/alert';
import { COLUNA_RETENCAO, ROTULO_CRITICADO, ROTULO_CRITICADO_CURTO, ROTULO_RETIDO, acaoDaLinha, opcoesDeMotivo, resumoRetidasEmLote, textosDoModal, tituloReterEmLote } from '../retencao-vocabulario';
import { BarraReterEmLote } from '../BarraReterEmLote';
import { marcarRetidas, selecionavel, useSelecaoEmLote } from '../selecao-em-lote';
import { RetencaoAviso } from '../RetencaoAviso';

interface IProps {
  people: IBeneficiary[];
  config: IConfig;
}

export function AppExclusionTable({ people, config }: IProps) {
  const { workspaceId, templateId, workspaceActivityId } = useParams() as { workspaceId: string, templateId: string, workspaceActivityId: string };
  const { openAlert } = useAlert();
  const history = useHistory();

  const [rows, setRows] = useState(people.map((row, index) => ({
    id: row?.id ?? `${index + 1}_${Date.now()}`,
    name: row?.name ?? '',
    taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '',
    taxPayerIdEmployee: !!row?.taxPayerIdEmployee ? cpfMask(row.taxPayerIdEmployee) : '',
    type: row?.type === "employee" ? 'Titular' : row?.type === "dependent" ? 'Dependente' : '',
    birthDate: !!row?.birthDate ? new Date(row.birthDate) : null,
    dismissalDate: !!row?.dismissalDate ? new Date(row.dismissalDate) : null,
    startPriorNoticeDate: !!row?.startPriorNoticeDate ? new Date(row.startPriorNoticeDate) : null,
    endPriorNoticeDate: !!row?.endPriorNoticeDate ? new Date(row.endPriorNoticeDate) : null,
    exclusionDateType: row?.exclusionDateType ?? null,
    effectiveExclusionDate: !!row?.effectiveExclusionDate ? new Date(row.effectiveExclusionDate) : null,
    plan: row?.plan ?? '',
    planContinuity: row?.planContinuity ?? null,
    isRetired: row?.isRetired ?? null,
    vigencyEndDate: !!row?.vigencyEndDate ? new Date(row.vigencyEndDate) : null,
    status: row?.status ?? '',
    documents: row?.documents ?? [],
    mandatoryDocuments: row?.mandatoryDocuments ?? [],
    retentionReason: row?.retentionReason ?? '',
    retentionNotes: row?.retentionNotes ?? '',
    dagRunId: row?.dagRunId,
  })));

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [modifiedRows, setModifiedRows] = useState<Set<GridRowId>>(new Set());

  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);

  // Atualizar rows quando people mudar (devido ao refetchInterval)
  useEffect(() => {
    // Só atualiza se não houver modificações pendentes
    if (modifiedRows.size === 0) {
      setRows(people.map((row, index) => ({
        id: row?.id ?? `${index + 1}_${Date.now()}`,
        name: row?.name ?? '',
        taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '',
        taxPayerIdEmployee: !!row?.taxPayerIdEmployee ? cpfMask(row.taxPayerIdEmployee) : '',
        type: row?.type === "employee" ? 'Titular' : row?.type === "dependent" ? 'Dependente' : '',
        birthDate: !!row?.birthDate ? new Date(row.birthDate) : null,
        dismissalDate: !!row?.dismissalDate ? new Date(row.dismissalDate) : null,
        startPriorNoticeDate: !!row?.startPriorNoticeDate ? new Date(row.startPriorNoticeDate) : null,
        endPriorNoticeDate: !!row?.endPriorNoticeDate ? new Date(row.endPriorNoticeDate) : null,
        exclusionDateType: row?.exclusionDateType ?? null,
        effectiveExclusionDate: !!row?.effectiveExclusionDate ? new Date(row.effectiveExclusionDate) : null,
        plan: row?.plan ?? '',
        planContinuity: row?.planContinuity ?? null,
        isRetired: row?.isRetired ?? null,
        vigencyEndDate: !!row?.vigencyEndDate ? new Date(row.vigencyEndDate) : null,
        status: row?.status ?? '',
        documents: row?.documents ?? [],
        mandatoryDocuments: row?.mandatoryDocuments ?? [],
        retentionReason: row?.retentionReason ?? '',
        retentionNotes: row?.retentionNotes ?? '',
        dagRunId: row?.dagRunId,
      })));
    }
  }, [people, modifiedRows.size]);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, Map<string, File>>>(new Map());
  const [retentionReason, setRetentionReason] = useState<string>('');
  const [retentionNotes, setRetentionNotes] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const lote = useSelecaoEmLote();
  // Retido em lote: ids do alvo enquanto o modal de Retido está aberto para o lote.
  const [alvoDoLote, setAlvoDoLote] = useState<GridRowId[] | null>(null);
  const [resumoLote, setResumoLote] = useState('');

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
    const updatedRow = { ...newRow } as any;

    // Só deriva status da data se o status não foi alterado explicitamente pelo usuário
    const statusChangedExplicitly = newRow.status !== oldRow.status;

    if (!statusChangedExplicitly && newRow.vigencyEndDate) {
      const vigencyDate = dayjs(newRow.vigencyEndDate);
      const today = dayjs();

      // Se a data de fim for no futuro, status = DELAYED (Em espera)
      // Caso contrário, status = READY (Pronto)
      if (vigencyDate.valueOf() > today.valueOf()) {
        updatedRow.status = 'DELAYED';
      } else {
        updatedRow.status = 'READY';
      }
    }

    setRows((prevRows) =>
      //@ts-ignore
      prevRows.map((row) => (row.id === newRow.id ? updatedRow : row))
    );

    setModifiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(newRow.id);
      return newSet;
    });

    console.log('Linha atualizada:', newRow.id, updatedRow);

    return updatedRow;
  };

  const handleOpenDocuments = (beneficiary: any) => {
    setSelectedBeneficiary(beneficiary);
    setIsModalUploadOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalUploadOpen(false);
    setSelectedBeneficiary(null);
  };

  const handleOpenRetainReleaseModal = (beneficiary: any) => {
    setSelectedBeneficiary(beneficiary);

    // Para status RETAINED ou SKIPED, buscar as informações salvas
    // Para outros status, deixar vazio
    const isRetainedOrSkipped = beneficiary?.status === 'RETAINED' || beneficiary?.status === 'SKIPED';
    setRetentionReason(isRetainedOrSkipped ? (beneficiary?.retentionReason ?? '') : '');
    setRetentionNotes(isRetainedOrSkipped ? (beneficiary?.retentionNotes ?? '') : '');
    setIsModalConfirmOpen(true);
  };

  const handleReterEmLote = () => {
    const ids = lote.alvo(rows);
    if (ids.length === 0) return;
    setAlvoDoLote(ids);
    setSelectedBeneficiary(null);
    setRetentionReason('');
    setRetentionNotes('');
    setResumoLote('');
    setIsModalConfirmOpen(true);
  };

  const handleCloseRetainReleaseModal = () => {
    setIsModalConfirmOpen(false);
    setAlvoDoLote(null);
    setSelectedBeneficiary(null);
    setRetentionReason('');
    setRetentionNotes('');
  };

  const handleConfirmRetainRelease = () => {
    if (alvoDoLote) {
      setRows(prevRows => marcarRetidas(prevRows, alvoDoLote, retentionReason, retentionNotes));
      setModifiedRows(prev => new Set([...prev, ...alvoDoLote]));
      setResumoLote(resumoRetidasEmLote(alvoDoLote.length));
      lote.limpar();
      handleCloseRetainReleaseModal();
      return;
    }

    const beneficiaryId = selectedBeneficiary?.id;
    if (!beneficiaryId) return;

    const isRetainedOrSkipped = selectedBeneficiary.status === 'RETAINED' || selectedBeneficiary.status === 'SKIPED';

    // Atualizar o status do beneficiário
    setRows(prevRows =>
      prevRows.map(row => {
        if (row.id === beneficiaryId) {
          return {
            ...row,
            status: isRetainedOrSkipped ? 'READY' : 'SKIPED',
            // Ao tirar a retenção ou marcar como corrigido, limpar as informações; ao reter, salvar as novas informações
            retentionReason: isRetainedOrSkipped ? '' : retentionReason,
            retentionNotes: isRetainedOrSkipped ? '' : retentionNotes,
          };
        }
        return row;
      })
    );

    // Marcar como modificado
    setModifiedRows(prev => new Set(prev).add(beneficiaryId));

    // Fechar modal
    handleCloseRetainReleaseModal();
  };

  const handleFileUpload = (documentType: string, file: File) => {
    const beneficiaryId = selectedBeneficiary?.id;

    if (!beneficiaryId) return;

    // Atualizar o Map de arquivos
    setUploadedFiles(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(beneficiaryId)) {
        newMap.set(beneficiaryId, new Map());
      }
      const beneficiaryFiles = newMap.get(beneficiaryId)!;
      beneficiaryFiles.set(documentType, file);
      return newMap;
    });

    // Atualizar a lista de documentos do beneficiário
    setRows(prevRows =>
      prevRows.map(row => {
        if (row.id === beneficiaryId) {
          const updatedDocuments = row.documents.includes(documentType)
            ? row.documents
            : [...row.documents, documentType];
          return { ...row, documents: updatedDocuments };
        }
        return row;
      })
    );

    // Marcar como modificado
    setModifiedRows(prev => new Set(prev).add(beneficiaryId));

    // Atualizar o beneficiário selecionado no estado
    setSelectedBeneficiary((prev: any) => ({
      ...prev,
      documents: prev.documents.includes(documentType)
        ? prev.documents
        : [...prev.documents, documentType]
    }));
  };

  const handleFileRemove = (documentType: string) => {
    const beneficiaryId = selectedBeneficiary?.id;

    if (!beneficiaryId) return;

    // Remover arquivo do Map se existir
    setUploadedFiles(prev => {
      const newMap = new Map(prev);
      const beneficiaryFiles = newMap.get(beneficiaryId);
      if (beneficiaryFiles) {
        beneficiaryFiles.delete(documentType);
        if (beneficiaryFiles.size === 0) {
          newMap.delete(beneficiaryId);
        }
      }
      return newMap;
    });

    // Remover documento da lista do beneficiário
    setRows(prevRows =>
      prevRows.map(row => {
        if (row.id === beneficiaryId) {
          const updatedDocuments = row.documents.filter(doc => doc !== documentType);
          return { ...row, documents: updatedDocuments };
        }
        return row;
      })
    );

    // Marcar como modificado
    setModifiedRows(prev => new Set(prev).add(beneficiaryId));

    // Atualizar o beneficiário selecionado no estado
    setSelectedBeneficiary((prev: any) => ({
      ...prev,
      documents: prev.documents.filter((doc: string) => doc !== documentType)
    }));
  };

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case 'READY':
        return {
          label: 'Pronto',
          backgroundColor: '#0288D114',
          color: '#01579B',
        };
      case 'SKIPED':
        return {
          label: ROTULO_RETIDO,
          backgroundColor: '#BA1A1A14',
          color: '#BA1A1A',
        };
      case 'DELAYED':
        return {
          label: 'Em espera',
          backgroundColor: '#EF6C0014',
          color: '#EF6C00',
        };
      case 'RETAINED':
        return {
          label: ROTULO_CRITICADO_CURTO,
          backgroundColor: '#0F0F1E14',
          color: '#0F0F1E',
        };
      default:
        return {
          label: status,
          backgroundColor: '#F5F5F5',
          color: '#616161',
        };
    }
  };

  const statusOptions = [
    { value: 'READY', label: 'Pronto' },
    { value: 'SKIPED', label: ROTULO_RETIDO },
    { value: 'DELAYED', label: 'Em espera' },
    { value: 'RETAINED', label: ROTULO_CRITICADO },
  ];

  const handleClearAll = () => {
    setResumoLote('');
    // Resetar todas as linhas para o estado original
    setRows(people.map((row, index) => ({
      id: row?.id ?? `${index + 1}_${Date.now()}`,
      name: row?.name ?? '',
      taxPayerId: !!row?.taxPayerId ? cpfMask(row.taxPayerId) : '',
      taxPayerIdEmployee: !!row?.taxPayerIdEmployee ? cpfMask(row.taxPayerIdEmployee) : '',
      type: row?.type === "employee" ? 'Titular' : row?.type === "dependent" ? 'Dependente' : '',
      birthDate: !!row?.birthDate ? new Date(row.birthDate) : null,
      dismissalDate: !!row?.dismissalDate ? new Date(row.dismissalDate) : null,
      startPriorNoticeDate: !!row?.startPriorNoticeDate ? new Date(row.startPriorNoticeDate) : null,
      endPriorNoticeDate: !!row?.endPriorNoticeDate ? new Date(row.endPriorNoticeDate) : null,
      exclusionDateType: row?.exclusionDateType ?? null,
      effectiveExclusionDate: !!row?.effectiveExclusionDate ? new Date(row.effectiveExclusionDate) : null,
      plan: row?.plan ?? '',
      planContinuity: row?.planContinuity ?? null,
      isRetired: row?.isRetired ?? null,
      vigencyEndDate: !!row?.vigencyEndDate ? new Date(row.vigencyEndDate) : null,
      status: row?.status ?? '',
      documents: row?.documents ?? [],
      mandatoryDocuments: row?.mandatoryDocuments ?? [],
      retentionReason: row?.retentionReason ?? '',
      retentionNotes: row?.retentionNotes ?? '',
      dagRunId: row?.dagRunId,
    })));

    // Limpar arquivos enviados
    setUploadedFiles(new Map());

    // Limpar modificações
    setModifiedRows(new Set());
  };

  const handleSaveAll = async () => {
    try {
      setIsSending(true);
      // Preparar FormData para enviar arquivos
      const formData = new FormData();

      // Pegar apenas as linhas modificadas
      const modifiedBeneficiaries = rows
        .filter((row) => modifiedRows.has(row.id))
        .map((row) => ({
          id: row.id,
          status: row.status,
          taxPayerId: row.taxPayerId.replace(/\D/g, ''),
          taxPayerIdEmployee: row.taxPayerIdEmployee.replace(/\D/g, ''),
          name: row.name,
          type: row.type === 'Titular' ? 'employee' : 'dependent',
          birthDate: row.birthDate,
          dismissalDate: row.dismissalDate,
          plan: row.plan,
          planContinuity: row.planContinuity,
          isRetired: row.isRetired,
          vigencyEndDate: row.vigencyEndDate,
          documents: row.documents,
          retentionReason: row.status === "SKIPED" ? (row.retentionReason || '') : '',
          retentionNotes: row.status === "SKIPED" ? (row.retentionNotes || '') : '',
          dagRunId: row.dagRunId,
        } as any));

      // Adicionar dados dos beneficiários ao FormData
      formData.append('beneficiaries', JSON.stringify(modifiedBeneficiaries));

      // Adicionar arquivos ao FormData
      modifiedBeneficiaries.forEach((beneficiary) => {
        const beneficiaryFiles = uploadedFiles.get(beneficiary.id);
        if (beneficiaryFiles) {
          beneficiaryFiles.forEach((file, documentType) => {
            formData.append(`file_${beneficiary.taxPayerId}_${documentType}`, file);
          });
        }
      });

      // Upload de arquivos para S3
      if (uploadedFiles.size > 0) {
        await api.post(`/v2/operation/files-upload/${workspaceActivityId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      await api.post(`/v2/apps/exclusion/${workspaceActivityId}`, {
        payload: modifiedBeneficiaries,
      })

      // Limpar modificações após salvar
      setModifiedRows(new Set());
      setUploadedFiles(new Map());
      openAlert('Dados salvos com sucesso.', 'success');
      history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/movements-check`)
    } catch(err) {
      openAlert('Erro ao salvar dados.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Nome completo",
      width: 350,
    },
    {
      field: "taxPayerId",
      headerName: "CPF beneficiário",
      width: 150,
    },
    {
      field: "taxPayerIdEmployee",
      headerName: "CPF titular",
      width: 150,
    },
    {
      field: "type",
      headerName: "Tipo beneficiário",
      width: 150,
    },
    {
      field: "birthDate",
      headerName: "Data de nascimento",
      width: 150,
      type: "date"
    },
    {
      field: "dismissalDate",
      headerName: "Data de demissão",
      width: 200,
      type: "date",
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return '-';
        const isUsed = params.row.exclusionDateType === 'DISMISSAL_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {new Date(params.value).toLocaleDateString('pt-BR')}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "endPriorNoticeDate",
      headerName: "Término Aviso Prévio",
      width: 220,
      type: "date",
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        const isUsed = params.row.exclusionDateType === 'PRIOR_NOTICE_END_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {new Date(params.value).toLocaleDateString('pt-BR')}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "isRetired",
      headerName: "Beneficiário aposentado",
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: true, label: 'Sim' },
        { value: false, label: 'Não' },
      ],
    },
    {
      field: "plan",
      headerName: "Plano",
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: config.allowedPlans,
    },
    {
      field: "planContinuity",
      headerName: "Continuidade de plano",
      width: 180,
      editable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: true, label: 'Sim' },
        { value: false, label: 'Não' },
      ],
    },
    {
      field: "vigencyEndDate",
      headerName: "Data de fim de plano",
      width: 200,
      editable: true,
      type: 'date',
    },
    {
      field: "documents",
      headerName: "Documentos",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        // D30: documento obrigatório que falta deixa a linha fora da aprovação.
        const faltam = documentosFaltando(params.row.mandatoryDocuments, params.row.documents);
        return (
          <Tooltip title={faltam.length ? `Faltam: ${faltam.join(", ")}` : ""}>
            <IconButton
              color="primary"
              onClick={() => handleOpenDocuments(params.row)}
              size="small"
              aria-label={faltam.length ? `Faltam ${faltam.length} documento(s)` : "Documentos"}
            >
              <Badge color="error" badgeContent={faltam.length}>
                <DescriptionIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: statusOptions, // {value, label} pairs
      renderCell: (params: GridRenderCellParams) => {
        const { label, backgroundColor, color } = getStatusChipProps(params.row.status);
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: backgroundColor,
              color: color,
              fontWeight: 500,
              cursor: params.row.status === 'DELAYED' ? 'pointer' : 'default',
            }}
          />
        );
      },
      renderEditCell: (params: any) => {
        const { id, field } = params;
        return (
          <Select
            value=""
            displayEmpty
            autoFocus
            size="small"
            fullWidth
            onChange={(e) => {
              params.api.setEditCellValue({ id, field, value: e.target.value });
              setTimeout(() => {
                params.api.stopCellEditMode({ id, field });
              }, 0);
            }}
          >
            <MenuItem value="READY">Pronto</MenuItem>
          </Select>
        );
      },
    },
    {
      field: "retainRelease",
      headerName: COLUNA_RETENCAO,
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isRetainedOrSkipped = params.row.status === 'RETAINED' || params.row.status === 'SKIPED';

        const acao = acaoDaLinha(params.row.status, "APROVACAO");

        return (
          <Tooltip title={acao}>
            <IconButton
              size="small"
              aria-label={acao}
              onClick={() => handleOpenRetainReleaseModal(params.row)}
              sx={{
                color: isRetainedOrSkipped ? '#2E7D32' : '#C62828'
              }}
            >
              {isRetainedOrSkipped ? (
                <VerifiedIcon />
              ) : (
                <BlockIcon />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const reasonOptions = ["Documentação Pendente"] as const;
  
  return (
    <>
      <Modal open={isModalUploadOpen} onClose={handleCloseModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <Typography variant="h6">Upload de arquivos</Typography>
            <Close
              style={{"cursor":"pointer"}}
              onClick={() => setIsModalUploadOpen(false)}
            />
          </div>
          <Typography variant="body1" style={{ marginBottom: "20px", marginTop: "5px"}}>Faça upload dos arquivos necessários</Typography>
          <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: "space-between", marginBottom: "20px" }}>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Nome</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.name}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do beneficiário</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerId}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do titular</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerIdEmployee}</span>
            </div>
          </div>
          <Typography variant="body1" style={{ marginBottom: "20px" }}><strong>Arquivos</strong></Typography>

          <div style={{ width: "100%", maxHeight: "400px", overflow: "scroll" }}>
            <List>
              {opcoesDeUpload(selectedBeneficiary?.mandatoryDocuments).map((document, index) => {
                const hasDocument = selectedBeneficiary?.documents?.includes(document);

                return (
                  <ListItem
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                  >
                    <ListItemText primary={document} secondary={selectedBeneficiary?.mandatoryDocuments?.includes(document) ? "Obrigatório" : undefined}/>
                    {!hasDocument ? (
                      <Button
                        variant="text"
                        component="label"
                        // startIcon={<UploadIcon />}
                      >
                        Fazer upload
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleFileUpload(document, file);
                            }
                          }}
                        />
                      </Button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: "rgb(48, 36, 225)", fontSize: "14px" }}>{document}</span>
                        {/* Só o arquivo escolhido agora e ainda não salvo sai; não há como apagar o já enviado,
                            e a lixeira dele fazia a tela dizer "falta" enquanto a aprovação aceitava o arquivo. */}
                        {uploadedFiles.get(selectedBeneficiary?.id)?.has(document) && (
                          <IconButton
                            size="small"
                            onClick={() => handleFileRemove(document)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" color='error' />
                          </IconButton>
                        )}
                      </div>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </div>
        </Box>
      </Modal>
      <Modal open={isModalConfirmOpen} onClose={handleCloseRetainReleaseModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <Typography variant="h6">{alvoDoLote ? tituloReterEmLote(rows.filter(r => alvoDoLote.includes(r.id)).map(r => r.name)) : textosDoModal(selectedBeneficiary?.status, "APROVACAO").titulo}</Typography>
            {/* <Close
              style={{"cursor":"pointer"}}
              onClick={() => setIsModalUploadOpen(false)}
            /> */}
          </div>
          <Typography variant="body1" style={{ marginBottom: "20px", marginTop: "5px"}}>{textosDoModal(selectedBeneficiary?.status, "APROVACAO").explicacao}</Typography>
          {!alvoDoLote && <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: "space-between", marginBottom: "20px" }}>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Nome</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.name}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do beneficiário</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerId}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do titular</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerIdEmployee}</span>
            </div>
          </div>}
          <RetencaoAviso status={selectedBeneficiary?.status} contexto="APROVACAO" />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="retention-reason-label">Motivo</InputLabel>
            <Select
              labelId="retention-reason-label"
              value={retentionReason}
              label="Motivo"
              onChange={(e) => setRetentionReason(e.target.value)}
              disabled={selectedBeneficiary?.status === 'RETAINED' || selectedBeneficiary?.status === 'SKIPED'}
            >
              {opcoesDeMotivo(reasonOptions, retentionReason).map(({ valor, somenteLeitura }) => (
                <MenuItem key={valor} value={valor} disabled={somenteLeitura}>
                  {valor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            label="Observações"
            value={retentionNotes}
            onChange={(e) => setRetentionNotes(e.target.value)}
            disabled={selectedBeneficiary?.status === 'RETAINED' || selectedBeneficiary?.status === 'SKIPED'}
            sx={{
              mb: 3,
              '& .MuiInputBase-root': {
                height: '240px',
                alignItems: 'flex-start',
              }
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant='outlined'
              color='error'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={handleCloseRetainReleaseModal}
              // disabled={isSendingUpdate}
            >
              Cancelar
            </Button>

            <Button
              variant='contained'
              style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={handleConfirmRetainRelease}
              disabled={
                // Se está tirando a retenção ou marcando como corrigido (RETAINED ou SKIPED), não desabilita
                // Se está retendo (status diferente), desabilita se não tiver motivo OU observação
                !(selectedBeneficiary?.status === 'RETAINED' || selectedBeneficiary?.status === 'SKIPED') &&
                (!retentionReason.trim() || !retentionNotes.trim())
              }
            >
              {textosDoModal(selectedBeneficiary?.status, "APROVACAO").botao}
            </Button>
          </Box>
        </Box>
      </Modal>
      <BarraReterEmLote quantidade={lote.alvo(rows).length} onReter={handleReterEmLote} resumo={resumoLote} />
      <DataGrid
        apiRef={lote.apiRef}
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={(p) => selecionavel(p.row.status)}
        rowSelectionModel={lote.modelo}
        onRowSelectionModelChange={lote.setModelo}
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        style={{ backgroundColor: "white", minHeight: 400 }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Erro ao atualizar linha:', error)}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        isCellEditable={(params) => {
          const status = params.row.status;

          // Campo status: editável apenas quando o beneficiário está DELAYED
          if (params.field === 'status') {
            return status === 'DELAYED';
          }

          const editableFields = ['plan', 'planContinuity', 'isRetired', 'vigencyEndDate'];

          // Se o campo não está na lista de campos editáveis condicionalmente, permite edição
          if (!editableFields.includes(params.field)) {
            return true;
          }

          // Para campos plan, planContinuity, isRetired e vigencyEndDate, bloqueia se status for SKIPED ou RETAINED
          return status !== 'SKIPED' && status !== 'RETAINED';
        }}
      />
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: "10px","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
        <Button
          variant='outlined'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={handleClearAll}
          disabled={modifiedRows.size === 0 || isSending}
        >
          Limpar
        </Button>
        <Button
          variant='contained'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={handleSaveAll}
          disabled={modifiedRows.size === 0 || isSending}
        >
          {!isSending ? "Salvar" : <CircularProgress size="20px"/>}
        </Button>
      </div>
    </>
  )
}
